import { useCallback, useState } from 'react'
import { killSwitchApi } from '@/services/api'
import { useUIStore } from '@/store/uiStore'
import { useTaskStore } from '@/store/taskStore'

export function useKillSwitch() {
  const [isActivating, setIsActivating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { killSwitchConfirmOpen, setKillSwitchConfirmOpen } = useUIStore()
  const { tasks, updateTask } = useTaskStore()

  const openConfirm = useCallback(() => {
    setKillSwitchConfirmOpen(true)
  }, [setKillSwitchConfirmOpen])

  const closeConfirm = useCallback(() => {
    setKillSwitchConfirmOpen(false)
  }, [setKillSwitchConfirmOpen])

  const activate = useCallback(
    async (reason?: string) => {
      setIsActivating(true)
      setError(null)

      try {
        await killSwitchApi.activate(reason)

        // Mark all running/pending tasks as cancelled locally
        tasks
          .filter((t) => t.status === 'running' || t.status === 'pending')
          .forEach((t) => {
            updateTask(t.id, { status: 'cancelled' })
          })

        setKillSwitchConfirmOpen(false)

        // Provide haptic feedback if available
        if ('vibrate' in navigator) {
          navigator.vibrate([100, 50, 100, 50, 200])
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to activate kill switch'
        setError(message)
      } finally {
        setIsActivating(false)
      }
    },
    [tasks, updateTask, setKillSwitchConfirmOpen]
  )

  const hasActiveTasks = tasks.some(
    (t) => t.status === 'running' || t.status === 'pending'
  )

  return {
    isConfirmOpen: killSwitchConfirmOpen,
    isActivating,
    error,
    hasActiveTasks,
    openConfirm,
    closeConfirm,
    activate,
  }
}
